import { ExclamationCircleOutlined, LockOutlined, MailOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Divider, Form, Input, Modal, message } from 'antd';
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useOptionalI18n } from '../i18n';
import apiClient from '../utils/axiosConfig';

interface LoginModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

interface LoginFormData {
  email: string;
  password: string;
}

interface RegisterFormData {
  name: string;
  email?: string;
  phoneNumber?: string;
  password: string;
  confirmPassword: string;
}

const LoginModal: React.FC<LoginModalProps> = ({
  visible,
  onCancel,
  onSuccess
}) => {
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const { login } = useAuth();
  const { t } = useOptionalI18n();

  const handleLogin = async (values: LoginFormData) => {
    console.log('handleLogin被调用，values:', values);
    try {
      setLoading(true);

      console.log('发送登录请求到:', '/auth/login');
      const response = await apiClient.post('/auth/login', {
        email: values.email,
        password: values.password
      });
      console.log('登录响应:', response.data);

      if (response.data.userExists && response.data.unactivated) {
        // 用户已存在但未激活，显示确认对话框
        Modal.confirm({
          title: '账号未激活',
          icon: <ExclamationCircleOutlined />,
          content: response.data.message,
          okText: '重新发送验证码',
          cancelText: '取消',
          onOk: async () => {
            try {
              setLoading(true);
              const resendResponse = await apiClient.post('/auth/resend-verification', {
                contact: response.data.email || response.data.phoneNumber,
                type: response.data.email ? 'EMAIL' : 'PHONE'
              });

              if (resendResponse.data.success) {
                message.success(t('messages.auth.verificationCodeResent'));
              } else {
                message.error(resendResponse.data.error || t('messages.auth.sendFailed'));
              }
            } catch (error: any) {
              message.error(error.response?.data?.error || t('messages.auth.sendFailed'));
            } finally {
              setLoading(false);
            }
          }
        });
      } else if (response.data.token) {
        const { token, user } = response.data;
        login(token, user);
        message.success(t('messages.auth.loginSuccess'));
        onSuccess?.();
        onCancel();
      } else {
        message.error(response.data.error || t('messages.auth.loginFailed'));
      }
    } catch (error: any) {
      console.error('登录错误:', error);
      message.error(error.response?.data?.error || t('messages.auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values: RegisterFormData) => {
    console.log('handleRegister被调用，values:', values);

    if (values.password !== values.confirmPassword) {
      message.error(t('messages.auth.passwordMismatch'));
      return;
    }

    // 验证邮箱或手机号至少填写一个
    if (!values.email && !values.phoneNumber) {
      message.error(t('messages.auth.pleaseEnterEmailOrPhone'));
      return;
    }

    // 如果同时填写了邮箱和手机号，只使用邮箱
    const registrationData = {
      name: values.name,
      email: values.email || '',
      password: values.password,
      confirmPassword: values.confirmPassword,
      phoneNumber: values.phoneNumber || ''
    };

    console.log('准备发送注册请求:', registrationData);

    try {
      setLoading(true);

      const response = await apiClient.post('/auth/register', registrationData);

      if (response.data.userExists && response.data.unactivated) {
        // 用户已存在但未激活，显示确认对话框
        Modal.confirm({
          title: '账号未激活',
          icon: <ExclamationCircleOutlined />,
          content: response.data.message,
          okText: '重新发送验证码',
          cancelText: '取消',
          onOk: async () => {
            try {
              setLoading(true);
              const resendResponse = await apiClient.post('/auth/resend-verification', {
                contact: response.data.email || response.data.phoneNumber,
                type: response.data.email ? 'EMAIL' : 'PHONE'
              });

              if (resendResponse.data.success) {
                message.success(t('messages.auth.verificationCodeResent'));
              } else {
                message.error(resendResponse.data.error || t('messages.auth.sendFailed'));
              }
            } catch (error: any) {
              message.error(error.response?.data?.error || t('messages.auth.sendFailed'));
            } finally {
              setLoading(false);
            }
          }
        });
      } else if (response.data.requiresVerification) {
        // 新用户注册成功，需要验证
        message.success(response.data.message);
      } else if (response.data.message) {
        message.success(t('messages.auth.registerSuccessPleaseLogin'));
        setIsLoginMode(true);
        registerForm.resetFields();
      } else {
        message.error(response.data.error || t('messages.auth.registerFailed'));
      }
    } catch (error: any) {
      message.error(error.response?.data?.error || t('messages.auth.registerFailed'));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    loginForm.resetFields();
    registerForm.resetFields();
  };

  return (
    <Modal
      title={isLoginMode ? '用户登录' : '用户注册'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={400}
    >
      {isLoginMode ? (
        <Form
          form={loginForm}
          layout="vertical"
          onFinish={handleLogin}
        >
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="请输入邮箱"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: '100%' }}
            >
              登录
            </Button>
          </Form.Item>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Button type="link" onClick={switchMode}>
              还没有账号？立即注册
            </Button>
          </div>
        </Form>
      ) : (
        <Form
          form={registerForm}
          layout="vertical"
          onFinish={handleRegister}
        >
          <Form.Item
            name="name"
            label="姓名"
            rules={[
              { required: true, message: '请输入姓名' },
              { min: 2, message: '姓名至少2个字符' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入姓名"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱（可选）"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(value)) {
                    return Promise.reject(new Error('请输入有效的邮箱地址'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="请输入邮箱（与手机号二选一）"
            />
          </Form.Item>

          <Form.Item
            name="phoneNumber"
            label="手机号（可选）"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const phoneRegex = /^1[3-9]\d{9}$/;
                  if (!phoneRegex.test(value)) {
                    return Promise.reject(new Error('请输入有效的手机号码'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="请输入手机号（与邮箱二选一）"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请输入密码"
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认密码"
            rules={[
              { required: true, message: '请确认密码' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="请再次输入密码"
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{ width: '100%' }}
            >
              注册
            </Button>
          </Form.Item>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Button type="link" onClick={switchMode}>
              已有账号？立即登录
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
};

export default LoginModal;
